import { apiGet } from '../../../utils/apiClient';

interface FetchStockDataOptions {
  accessToken: string;
  apiBaseUrl: string;
  refreshAccessToken: () => Promise<string | null>;
}

interface StockDataResponse {
  serverData: any[];
  stockCode: string;
  stockName: string;
}

/**
 * 백엔드 응답 형식 (ResponseInterceptor가 래핑한 후)
 */
interface StockDataResponseDto {
  stockCode: string;
  stockName: string;
  data: any[];
}

/**
 * 랜덤 일봉 데이터를 서버에서 가져옴
 * apiClient를 사용하여 백엔드의 공통 응답 구조를 자동으로 처리합니다.
 */
export const fetchRandomDailyStockData = async (
  options: FetchStockDataOptions
): Promise<StockDataResponse> => {
  const { apiBaseUrl } = options;

  console.log('📡 API 호출 시작: get-random-daily-stock-data');

  try {
    // apiClient를 사용하여 백엔드의 공통 응답 구조를 자동으로 처리
    // apiGet은 ResponseInterceptor가 래핑한 응답에서 자동으로 data를 추출합니다.
    const responseData: any = await apiGet<any>(
      `${apiBaseUrl}/api/stock-data/get-random-daily-stock-data`
    );

    console.log('✅ API 응답 성공 (전체 데이터):', responseData);
    console.log('✅ API 응답 성공 (데이터 타입):', typeof responseData);
    console.log(
      '✅ API 응답 성공 (키 목록):',
      responseData ? Object.keys(responseData) : 'null'
    );
    console.log('✅ API 응답 성공 (상세):', {
      stockCode: responseData?.stockCode,
      stockName: responseData?.stockName,
      stockNameType: typeof responseData?.stockName,
      stockNameIsCode: responseData?.stockName === responseData?.stockCode,
      hasData: !!responseData?.data,
      data: responseData?.data,
      dataLength: responseData?.data?.length || 0,
      dataType: typeof responseData?.data,
      isArray: Array.isArray(responseData?.data),
      allKeys: responseData ? Object.keys(responseData) : [],
    });

    // 응답 구조 확인 및 파싱
    let stockCode: string;
    let stockName: string;
    let serverData: any[];

    // 경우 1: 직접 StockDataResponseDto 형식
    if (responseData?.stockCode && responseData?.data) {
      stockCode = responseData.stockCode;
      // 서버에서 반환한 종목명을 그대로 사용 (서버에서 이미 검증함)
      stockName = responseData.stockName || responseData.stockCode;
      serverData = Array.isArray(responseData.data) ? responseData.data : [];
      console.log('📦 경우 1 - 직접 형식:', {
        stockCode,
        stockName,
        originalStockName: responseData.stockName,
        stockNameIsCode: responseData.stockName === responseData.stockCode,
      });
    }
    // 경우 2: 중첩된 구조 (data.data)
    else if (responseData?.data?.stockCode && responseData?.data?.data) {
      stockCode = responseData.data.stockCode;
      // 서버에서 반환한 종목명을 그대로 사용 (서버에서 이미 검증함)
      stockName = responseData.data.stockName || responseData.data.stockCode;
      serverData = Array.isArray(responseData.data.data)
        ? responseData.data.data
        : [];
      console.log('📦 경우 2 - 중첩 구조:', {
        stockCode,
        stockName,
        originalStockName: responseData.data.stockName,
        stockNameIsCode:
          responseData.data.stockName === responseData.data.stockCode,
      });
    }
    // 경우 3: 배열이 직접 반환된 경우 (백엔드 버그로 인한 임시 처리)
    else if (Array.isArray(responseData) && responseData.length > 0) {
      console.warn(
        '⚠️ 배열이 직접 반환되었습니다. 첫 번째 항목에서 stockCode를 추출합니다.'
      );
      // 배열의 첫 번째 항목에서 stockCode 추출
      const firstItem = responseData[0];
      stockCode = firstItem?.stockCode || '';
      stockName = firstItem?.stockName || stockCode;
      serverData = responseData;
    } else {
      console.error('❌ 응답 데이터 형식 오류:', responseData);
      throw new Error('응답 데이터 형식이 올바르지 않습니다.');
    }

    if (!stockCode || !serverData || serverData.length === 0) {
      throw new Error('응답 데이터가 비어있거나 유효하지 않습니다.');
    }

    console.log('📦 최종 추출된 데이터:', {
      stockCode,
      stockName,
      stockNameIsCode: stockName === stockCode,
      serverDataLength: serverData.length,
    });

    return {
      serverData,
      stockCode,
      stockName,
    };
  } catch (error) {
    console.error('❌ API 호출 실패:', error);
    throw error;
  }
};
